<template>
  <v-app :class="['route-' + $route.name]">
    <v-sheet :color="$store.state.background" class="h-100 d-flex">
      <v-app-bar
        :color="$vuetify.breakpoint.lgAndUp ? 'transparent' : 'background'"
        :flat="$vuetify.breakpoint.lgAndUp"
        class="shrink"
        app
      >
        <v-app-bar-nav-icon
          v-if="$vuetify.breakpoint.smAndDown"
          class="mr-3"
          @click="onAppBarNavClick"
        />

        <span
          class="highlighted-text display-1 font-weight-black px-3 rounded"
          style="font-family: 'Roboto', sans-serif !important"
        >DM</span>

        <v-spacer />

        <div class="d-flex align-center" :style="{
          gap: $vuetify.breakpoint.mdAndUp ? '1rem' : 0,
        }">

          <v-btn
            v-if="$vuetify.breakpoint.mdAndUp"
            :to="{ name: 'donate' }"
            :disabled="$route.name === 'donate'"
            color="secondary"
          >
            <v-icon left>mdi-hand-coin-outline</v-icon>
            {{ $t('route.donate') }}
          </v-btn>

          <v-btn
            v-if="$vuetify.breakpoint.mdAndUp"
            :href="retreatPdf"
            target="_blank"
            color="primary"
          >
            <v-icon left>mdi-download</v-icon>
            {{ $t('btn.retreat') }}
          </v-btn>

          <v-tabs
            v-if="$vuetify.breakpoint.mdAndUp"
            v-model="routeName"
            style="max-width: max-content"
            optional
            color="white"
            background-color="transparent"
          >
            <v-tab
              v-for="link in links.filter(link => link.section === 'secondary')"
              :key="link.key"
              :to="{ name: link.key }"
              exact
            >
              <v-icon left>{{ link.icon }}</v-icon>
              <span>{{ $t('route.' + link.key) }}</span>
            </v-tab>
          </v-tabs>

          <v-tooltip
            v-for="link in socialLinks"
            :key="link.href"
            bottom
            color="primary"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                :href="link.href"
                target="_blank"
                icon
                large
              >
                <v-icon v-text="link.icon"></v-icon>
              </v-btn>
            </template>
            <span v-text="link.name"></span>
          </v-tooltip>
        </div>

        <template v-if="$vuetify.breakpoint.mdAndUp">
          <v-divider class="mx-4" inset vertical />

          <v-select
            v-model="$i18n.locale"
            :items="availableLanguages"
            style="max-width: 10rem"
            prepend-inner-icon="mdi-translate"
            hide-details
            class="select-transparent"
            flat
            solo
            dense
          />
        </template>
      </v-app-bar>

      <v-main class="grow">
        <transition name="slide-left" mode="out-in">
          <router-view
            :key="$route.fullPath"
            class="w-100"
          ></router-view>
        </transition>
      </v-main>

      <v-app-bar v-if="$vuetify.breakpoint.mdAndUp" color="background" class="shrink" app bottom>
        <v-tabs v-model="routeName" grow slider-size="5" background-color="primary" optional>
          <v-tab
            v-for="link in links.filter(link => link.section === 'primary')"
            :key="link.key"
            :to="{ name: link.key }"
            :exact-path="link.key === 'home'"
          >
            <v-icon left>{{ link.icon }}</v-icon>
            <div style="transform: translateY(0.1rem)">{{ $t('route.' + link.key) }}</div>
          </v-tab>
        </v-tabs>
      </v-app-bar>
    </v-sheet>

    <Sidebar v-model="drawer" />
  </v-app>
</template>

<script lang="ts">
import { Vue, Component, Watch } from 'vue-property-decorator';
import Sidebar from '@/components/Sidebar.vue';
import { availableLanguages, retreatPdf, links } from '@/global';

@Component({
  components: {
    Sidebar,
  }
})
export default class HomeView extends Vue {
  drawer = false
  links = links
  availableLanguages = availableLanguages
  retreatPdf = retreatPdf
  routeName = null

  @Watch('$i18n.locale')
  onI18nLocaleChange(locale: string) {
    localStorage.setItem('locale', locale);
    this.updateTitle();
  }

  @Watch('$route.fullPath', { immediate: true })
  onRouteFullPathChange() {
    this.updateTitle();
  }

  get socialLinks() {
    return [
      { href: this.$jms('settings.instagram'), icon: 'mdi-instagram', name: 'Instagram' },
      { href: this.$jms('settings.facebook'), icon: 'mdi-facebook', name: 'Facebook' },
      { href: this.$jms('settings.youtube'), icon: 'mdi-youtube', name: 'Youtube' },
      { href: this.$jms('settings.soundCloud'), icon: 'mdi-soundcloud', name: 'SoundCloud' },
    ]
  }

  updateTitle() {
    let title = (this.$i18n.t('route.' + this.$route.name) || 'Untitled') + ' - Dolama Mantas Jacikas';
    Object.keys(this.$route.params).forEach(key => {
      title = (this.$i18n.t('routeParams.' + this.$route.name + '.' + key + '.' + this.$route.params[key]) || 'Untitled') + ' - ' + title;
    })
    document.title = title;

    const metaDescElement = document.head.querySelector('meta[name=description]');
    if (metaDescElement) {
      const description = this.$i18n.t('meta.description.' + this.$route.name).toString();
      metaDescElement.setAttribute('content', description);
    }

    const htmlElement = document.querySelector('html');
    if (htmlElement) {
      htmlElement.setAttribute('lang', (this.$i18n.locale || '').substring(0, 2))
    }
  }

  onAppBarNavClick() {
    this.drawer = !this.drawer;
  }
}
</script>
